'use client';
// L'ESPACE DOCUMENTS D'UNE MAINTENANCE (RQ, 28/10/2026).
//
// « Je veux un espace pour charger des documents dans la maintenance, et dire
// quel type de document c'est — rapport d'intervention, devis, facture… »
//
// Un seul composant pour DEUX supports : une intervention de la fiche de vie et
// l'acquittement d'un acte du plan. Il ne sait rien d'eux — il reçoit les
// fichiers et trois gestes (joindre, retirer, préciser le type) — pour que la
// fiche de vie et la fenêtre de l'historique ne divergent jamais.
//
// Ce qu'il refuse de faire :
//  · Joindre sans type. Le type se choisit AVANT le fichier : un document dont
//    on ignore la nature oblige l'auditeur à tous les ouvrir.
//  · Cacher un fichier sans type. Ceux joints avant le 28/10/2026 n'en ont pas :
//    ils s'affichent « type non précisé », en ambre, avec de quoi le préciser.
//  · Effacer. Un fichier retiré demande un motif, reste listé (qui, quand,
//    pourquoi) et reste sur le disque.
import React, { useRef, useState } from 'react';
import { fmtDate } from '@/lib/meta';
import { inputStyle, I } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { lbl } from './commun';
import { TYPES_DOCUMENT, libelleTypeDocument } from '@/lib/documents-maintenance';

/** Un fichier joint, dans la forme que les deux supports ont en commun. */
export interface DocJoint {
  cle: string; name: string; ext: string; size: number;
  ajouteLe?: string; ajouteParNom?: string;
  typeDoc?: string;
  retireLe?: string; retireParNom?: string; motifRetrait?: string;
}

const octets = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);
const urlFichier = (f: DocJoint) => `/api/ged/ged/equip/fichiers/${encodeURIComponent(f.cle)}/${encodeURIComponent(f.ext)}`;

export function DocumentsMaintenance({ fichiers, peutModifier, rapportAttendu = false, onJoindre, onRetirer, onQualifier, idBase }: {
  fichiers: DocJoint[];
  /** Joindre, retirer, préciser le type. Faux = lecture seule. */
  peutModifier: boolean;
  /** Un code de rapport est inscrit : sans fichier en face, c'est une déclaration sans preuve — on le dit. */
  rapportAttendu?: boolean;
  onJoindre: (fichier: File, typeDoc: string) => Promise<boolean>;
  onRetirer: (cle: string, motif: string) => Promise<boolean>;
  onQualifier: (cle: string, typeDoc: string) => Promise<boolean>;
  /** Préfixe des identifiants de champs, unique par support (plusieurs interventions sur la même page). */
  idBase: string;
}) {
  const actifs = fichiers.filter((f) => !f.retireLe);
  const retires = fichiers.filter((f) => f.retireLe);
  const [typeChoisi, setTypeChoisi] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [aRetirer, setARetirer] = useState<string | null>(null);
  const [motif, setMotif] = useState('');
  const [occupe, setOccupe] = useState(false);
  const entree = useRef<HTMLInputElement>(null);

  // Envoi UN PAR UN : l'ordre de la liste suit l'ordre choisi.
  const joindre = async (liste: File[]) => {
    setEnvoi(true);
    try { for (const f of liste) await onJoindre(f, typeChoisi); } finally { setEnvoi(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {actifs.length === 0 && (
        <span style={{ fontSize: 12.5, color: rapportAttendu ? 'var(--amber-600)' : 'var(--text-muted)', fontWeight: rapportAttendu ? 600 : 400, fontStyle: rapportAttendu ? 'normal' : 'italic' }}>
          {rapportAttendu ? 'Rapport non fourni : un code de rapport est inscrit, sans document en face.' : 'Aucun document joint.'}
        </span>
      )}
      {actifs.map((f) => (
        <div key={f.cle} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
          {peutModifier ? (
            <select aria-label={`Type du document ${f.name}`} value={f.typeDoc || ''} disabled={occupe}
              onChange={(ev) => {
                if (!ev.target.value) return;
                setOccupe(true);
                void onQualifier(f.cle, ev.target.value).finally(() => setOccupe(false));
              }}
              style={{ ...inputStyle, width: 'auto', minWidth: 200, fontSize: 12.5, padding: '4px 8px', ...(f.typeDoc ? {} : { color: 'var(--amber-600)', fontWeight: 600 }) }}>
              {!f.typeDoc && <option value="">Type non précisé — à préciser</option>}
              {TYPES_DOCUMENT.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
            </select>
          ) : (
            <span style={{ fontWeight: 700, color: f.typeDoc ? 'var(--text-strong)' : 'var(--amber-600)', minWidth: 200 }}>{libelleTypeDocument(f.typeDoc)}</span>
          )}
          <a href={urlFichier(f)} target="_blank" rel="noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 600, color: 'var(--color-primary-active)', textDecoration: 'none' }}>
            <I n="doc" s={13} />{f.name}
          </a>
          <span style={{ color: 'var(--text-muted)' }}>
            {octets(f.size)}{f.ajouteLe ? ` · joint le ${fmtDate(f.ajouteLe)}` : ''}{f.ajouteParNom ? ` par ${f.ajouteParNom}` : ''}
          </span>
          {peutModifier && aRetirer !== f.cle && (
            <Bouton taille="sm" variante="discret" aria-label={`Retirer ${f.name}`}
              title="Retirer ce fichier — motif demandé, retrait tracé au journal de l’appareil"
              onClick={() => { setARetirer(f.cle); setMotif(''); }} disabled={occupe}>
              <I n="x" s={12} />
            </Bouton>
          )}
        </div>
      ))}

      {aRetirer && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 10 }}>
          <div style={{ flex: '1 1 260px' }}>
            <label style={lbl} htmlFor={`${idBase}-motif`}>Pourquoi retirer « {actifs.find((f) => f.cle === aRetirer)?.name} » ?</label>
            <input id={`${idBase}-motif`} value={motif} maxLength={500} onChange={(ev) => setMotif(ev.target.value)}
              placeholder="Mauvais fichier, doublon, rapport remplacé par la version signée…" style={inputStyle} />
          </div>
          <Bouton taille="sm" onClick={() => setARetirer(null)} disabled={occupe}>Annuler</Bouton>
          <Bouton variante="danger" taille="sm" disabled={occupe || !motif.trim()}
            onClick={() => {
              setOccupe(true);
              void onRetirer(aRetirer, motif.trim()).then((ok) => { if (ok) setARetirer(null); }).finally(() => setOccupe(false));
            }}>
            {occupe ? 'Retrait…' : 'Retirer le fichier'}
          </Bouton>
        </div>
      )}

      {/* Un fichier retiré n'est pas effacé : on dit qu'il l'a été, par qui et pourquoi. */}
      {retires.length > 0 && (
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {retires.map((f) => `${f.name} (${libelleTypeDocument(f.typeDoc)}) retiré le ${fmtDate(f.retireLe || '')}${f.retireParNom ? ` par ${f.retireParNom}` : ''} — ${f.motifRetrait || 'sans motif'}`).join(' · ')}
        </span>
      )}

      {peutModifier && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', paddingTop: 4 }}>
          <div>
            <label style={lbl} htmlFor={`${idBase}-type`}>Type du document à ajouter</label>
            <select id={`${idBase}-type`} value={typeChoisi} onChange={(ev) => setTypeChoisi(ev.target.value)}
              style={{ ...inputStyle, width: 'auto', minWidth: 230 }}>
              <option value="">— choisir le type —</option>
              {TYPES_DOCUMENT.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
            </select>
          </div>
          <input ref={entree} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: 'none' }}
            onChange={(ev) => {
              const liste = Array.from(ev.target.files || []);
              if (liste.length) void joindre(liste);
              ev.target.value = '';
            }} />
          {/* Le bouton ne s'ouvre qu'une fois le type choisi : c'est ce qui rend le type obligatoire sans message d'erreur. */}
          <Bouton taille="sm" disabled={!typeChoisi || envoi} onClick={() => entree.current?.click()}
            title={typeChoisi ? 'PDF, JPEG ou PNG, 25 Mo au plus chacun' : 'Choisissez d’abord le type du document'}>
            <I n="plus" s={12} />{envoi ? 'Envoi…' : 'Ajouter un document'}
          </Bouton>
        </div>
      )}
    </div>
  );
}
